(() => {
  if (window.__armadaWishlistStateBound) return;
  window.__armadaWishlistStateBound = true;

  const NAV_SELECTOR = '[data-wishlist-nav]';
  const TOGGLE_SELECTOR = '[data-wishlist-toggle][data-product-handle]';
  const COUNT_KEY = '__armadaWishlistCountHint';
  // Stamped by applyToggleHint, read by the app-count watcher further down
  // (AAS-5619) — declared here because it is written before it is observed.
  let lastLocalChangeAt = 0;
  const HANDLES_KEY = '__armadaWishlistHandlesHint';

  const isOn = (el) =>
    el.classList.contains('is-wishlisted') || el.getAttribute('aria-pressed') === 'true';

  const readCount = () => {
    try {
      const raw = localStorage.getItem(COUNT_KEY);
      const n = raw == null ? 0 : parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  };

  const writeCount = (n) => {
    try {
      localStorage.setItem(COUNT_KEY, String(Math.max(0, n)));
    } catch {}
  };

  const readHandles = () => {
    try {
      const raw = localStorage.getItem(HANDLES_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter((entry) => entry && entry.handle) : [];
    } catch {
      return [];
    }
  };

  const writeHandles = (handles) => {
    try {
      localStorage.setItem(HANDLES_KEY, JSON.stringify(handles.slice(0, 100)));
    } catch {}
  };

  const addHandle = (btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    const variantId = btn.dataset.variantId || '';
    const handles = readHandles();
    // AAS-5630: one entry per product, matching the app's product-level mode,
    // but the variant is refreshed rather than kept — re-saving a product in a
    // different colour used to leave the first colour behind.
    const existing = handles.find((entry) => entry.handle === handle);
    if (existing) {
      if (existing.variantId === variantId) return;
      existing.variantId = variantId;
    } else {
      handles.push({ handle, variantId });
    }
    writeHandles(handles);
  };

  const removeHandle = (btn) => {
    const handle = btn.dataset.productHandle;
    if (!handle) return;
    writeHandles(readHandles().filter((entry) => entry.handle !== handle));
  };

  /* ------------------------------------------------------------------
     AAS-5630: keep the toggle pointing at the variant on display.

     `data-variant-id` is what swish-headless-wishlist-button.js hands the app,
     and Liquid can only stamp it with the variant the page was rendered for.
     Choosing a colour on a tile, or a variant on a product page, used to leave
     it untouched, so the app faithfully saved the variant nobody selected.

     Callers pass the region they own, because a page carries toggles for many
     products at once — a product page also lists recommendations. `handle`
     narrows that further for callers whose scope cannot be tightened.
     ------------------------------------------------------------------ */
  const syncVariant = (scope, variantId, handle) => {
    if (!variantId) return;

    const root = scope || document;
    const targets = root.matches?.(TOGGLE_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll?.(TOGGLE_SELECTOR) ?? []);

    targets.forEach((btn) => {
      if (handle && btn.dataset.productHandle !== handle) return;
      if (btn.dataset.variantId === String(variantId)) return;

      btn.dataset.variantId = String(variantId);
      /* The app reads the attribute when the click is handled, so nothing has
         to be re-registered. Announcing the change anyway keeps anything else
         listening to the toggle (the count hint below included) in step. */
      btn.dispatchEvent(
        new CustomEvent('armada:wishlist-variant-change', {
          bubbles: true,
          detail: { productHandle: btn.dataset.productHandle, variantId: String(variantId) },
        })
      );
    });
  };

  window.armadaSyncWishlistVariant = syncVariant;

  const applyNav = (count) => {
    const filled = count > 0;
    // AAS-4682: the icon is hidden entirely while the wishlist is empty. The
    // class is set pre-paint by the inline script in theme.liquid; keep it in
    // sync here so adding the first item reveals the icon (and removing the
    // last one hides it) without a reload.
    document.documentElement.classList.toggle('wishlist-has-items', filled);
    document.querySelectorAll(NAV_SELECTOR).forEach((el) => {
      el.classList.toggle('is-wishlisted', filled);
      el.dataset.wishlistCount = String(count);
    });
  };

  const applyToggleHint = (btn, nowOn, wasOn) => {
    if (nowOn === wasOn || btn.__armadaWishlistHintState === nowOn) return;

    lastLocalChangeAt = Date.now();

    const next = nowOn ? readCount() + 1 : Math.max(0, readCount() - 1);
    writeCount(next);
    applyNav(next);

    if (nowOn) {
      addHandle(btn);
    } else {
      removeHandle(btn);
    }

    btn.__armadaWishlistHintState = nowOn;
  };

  /* ------------------------------------------------------------------
     AAS-5619: let the count come back DOWN.

     Everything above moves the hint on a local action: applyToggleHint when a
     tile is clicked, and armada-wishlist-page.js rewrites it wholesale while
     the customer is on the wishlist page. Nothing carried a correction back
     when the truth changed anywhere else — a dropped Swish session, a second
     device, a list cleared by the app — and syncFromVisibleToggles below can
     only ever RAISE the count. So a hint that was too high stayed too high,
     and the header icon sat over an empty wishlist on every page until the
     customer happened to open the wishlist page.

     The app already publishes the real number: swish-headless-wishlist-nav.js
     writes `data-wishlist-count` on the nav link from the wishlist observable.
     Watching that attribute needs no extra request and no coupling to Swish's
     internal JS API — only to our own component's contract.
     ------------------------------------------------------------------ */
  const ZERO_SETTLE_MS = 1500;
  const LOCAL_CHANGE_GRACE_MS = 3000;
  let zeroTimer = null;

  const observeAppCount = () => {
    const navs = document.querySelectorAll(NAV_SELECTOR);
    if (!navs.length) return;

    const readNav = (el) => {
      const raw = el.getAttribute('data-wishlist-count');
      const n = raw == null ? null : parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 ? n : null;
    };

    const onValue = (el) => {
      const count = readNav(el);
      // Our own applyNav writes this attribute too; it always matches the hint
      // it just stored, so there is nothing to correct and nothing to do.
      if (count == null || count === readCount()) return;

      clearTimeout(zeroTimer);
      zeroTimer = null;

      if (count > 0) {
        writeCount(count);
        applyNav(count);
        return;
      }

      /* A zero is ambiguous. The wishlist observable seeds
         `{ id: 'mine', items: [], numItems: 0 }` and only emits again once the
         network answers, so the first zero of every page load is indistinguish-
         able from a genuinely empty wishlist. Acting on it at once would hide
         the icon on every load and show it again a moment later — trading this
         bug for a more visible one. A zero is believed only after it has stood
         unchallenged, and never straight after a local change the app has not
         caught up with yet. Any real change clears the timer above. */
      const wait = Math.max(
        ZERO_SETTLE_MS,
        LOCAL_CHANGE_GRACE_MS - (Date.now() - lastLocalChangeAt)
      );
      zeroTimer = setTimeout(() => {
        if (readNav(el) !== 0) return;
        writeCount(0);
        applyNav(0);
      }, wait);
    };

    navs.forEach((el) => {
      new MutationObserver(() => onValue(el)).observe(el, {
        attributes: true,
        attributeFilter: ['data-wishlist-count'],
      });
      onValue(el);
    });
  };

  const syncFromVisibleToggles = () => {
    const onCount = document.querySelectorAll(
      '[data-wishlist-toggle].is-wishlisted, [data-wishlist-toggle][aria-pressed="true"]'
    ).length;
    if (onCount > readCount()) {
      writeCount(onCount);
      applyNav(onCount);
    }
  };

  applyNav(readCount());

  const initialSync = () => {
    observeAppCount();
    syncFromVisibleToggles();
    setTimeout(syncFromVisibleToggles, 600);
    setTimeout(syncFromVisibleToggles, 1500);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialSync);
  } else {
    initialSync();
  }

  document.addEventListener(
    'click',
    (event) => {
      const btn =
        event.target && event.target.closest
          ? event.target.closest(TOGGLE_SELECTOR)
          : null;
      if (!btn) return;

      const wasOn = isOn(btn);
      btn.__armadaWishlistHintState = wasOn;

      let attempts = 0;
      const poll = setInterval(() => {
        attempts += 1;
        const nowOn = isOn(btn);

        if (nowOn !== wasOn) {
          clearInterval(poll);
          applyToggleHint(btn, nowOn, wasOn);
        } else if (attempts > 30) {
          clearInterval(poll);
        }
      }, 60);
    },
    true
  );

  document.addEventListener('armada:wishlist-state-change', (event) => {
    if (!event.detail || event.detail.optimistic !== true) return;

    const btn =
      event.target && event.target.closest
        ? event.target.closest(TOGGLE_SELECTOR)
        : null;
    if (!btn) return;

    const nowOn = event.detail.inWishlist === true;
    const wasOn =
      typeof btn.__armadaWishlistHintState === 'boolean'
        ? btn.__armadaWishlistHintState
        : !nowOn;

    applyToggleHint(btn, nowOn, wasOn);
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== COUNT_KEY) return;
    applyNav(readCount());
  });
})();
